import React from 'react';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames/bind';
import { Button, Spin } from 'grab-ui';
import {
  SCRIBE_SHOPIFY_STATES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import { getCompanyID } from 'helper/user';
import useNotification, {
  makeErrorMessage,
} from 'views/v2/shopify-home/hooks/useNotification';
import { updateShopifyConfigurationFinancialStatus } from 'actions/shopify-endpoints';
import PageContentPanel from 'components/v2/page-content-panel';
import { ShopContext } from 'views/v2/shopify-home/contexts/shop-context';
import styles from './shop-financial-status-settings-section.less';

const cx = classnames.bind(styles);

const localeKeyPrefix = 'shopify.settings.financialStatusSection';

export const MODE = {
  READ: 'READ',
  EDIT: 'EDIT',
};

const makeInitialForm = (fieldNamesObj, valuesObj) => {
  const valuesUpper = Object.entries(valuesObj)
    .filter(([k, v]) => v === true)
    .map(([k, _]) => k.toUpperCase());

  // compare with FIELD_NAME keys
  return Object.keys(fieldNamesObj).reduce(
    (a, v) => ({ ...a, [v]: valuesUpper.indexOf(v) !== -1 }),
    {},
  );
};

// convert object keys to array of string, filter by 'true' value
const makeSelectedKeyArray = statusForm =>
  Object.entries(statusForm).reduce(
    (a, [k, v]) => (v ? [...a, k.toLowerCase()] : [...a]),
    [],
  );

const ShopFinancialStatusSettingsSection = ({ dispatch, countryCode }) => {
  const FIELD_NAME = {
    PAID: `${I18n.t(`${localeKeyPrefix}.form.fieldLabel.paid`)}`,
    PENDING: `${I18n.t(`${localeKeyPrefix}.form.fieldLabel.pending`)}`,
  };

  const trackCtx = React.useContext(ShopifyTrackEventContext);

  const fireEditOrderSyncTrackEvent = previousOrderSyncStatuses => {
    trackCtx.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.EDIT_ORDER_SYNC,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.PREVIOUS_ORDER_SYNC_STATUS]: previousOrderSyncStatuses,
      },
    });
  };

  const fireSaveOrderSyncTrackEvent = (
    previousOrderSyncStatuses,
    newOrderSyncStatuses,
  ) => {
    trackCtx.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.SAVE_ORDER_SYNC,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.PREVIOUS_ORDER_SYNC_STATUS]: previousOrderSyncStatuses,
        [SCRIBE_SHOPIFY_PARAMS.NEW_ORDER_SYNC_STATUS]: newOrderSyncStatuses,
      },
    });
  };

  const { shop, userInfo, refetchShops } = React.useContext(ShopContext);

  const shopIdentifier = shop.shop;
  const defaultFinancialStatuses = shop.financialStatus || {};
  const defaultForm = makeInitialForm(FIELD_NAME, defaultFinancialStatuses);

  const { showNotification } = useNotification(dispatch);
  const [mode, setMode] = React.useState(MODE.READ);
  const [loading, setLoading] = React.useState(false);
  const [statusForm, setStatusForm] = React.useState(defaultForm);

  const shouldDisableFormField = mode === MODE.READ || loading;

  const toggleOption = key => {
    setStatusForm(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const saveHandler = () => {
    setLoading(true);

    const companyID = getCompanyID(userInfo) || null;
    const financialStatuses = makeSelectedKeyArray(statusForm);

    const requestParams = {
      shop: shopIdentifier,
      countryCode,
      financialStatuses,
    };

    fireSaveOrderSyncTrackEvent(
      makeSelectedKeyArray(defaultFinancialStatuses),
      requestParams.financialStatuses,
    );

    updateShopifyConfigurationFinancialStatus(requestParams, companyID)
      .then(success => {
        if (!success) {
          showNotification(
            makeErrorMessage('Settings not updated. An error has occurred.'),
          );
          return;
        }
        refetchShops();
        setMode(MODE.READ);
      })
      .catch(async err => {
        const { message } = await err.json();
        showNotification(makeErrorMessage(message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const makeActionButtons = (mode, disabled) => {
    switch (mode) {
      case MODE.EDIT:
        return [
          <Button
            key={1}
            type="link"
            htmlType="submit"
            className={styles.ActionButton}
            disabled={disabled}
            onClick={saveHandler}
          >
            {I18n.t('v2.commonButton.save')}
          </Button>,
          <Button
            key={2}
            type="link"
            className={styles.ActionButton}
            disabled={disabled}
            onClick={() => {
              setStatusForm(defaultForm);
              setMode(MODE.READ);
            }}
          >
            {I18n.t('v2.commonButton.cancel')}
          </Button>,
        ];
      case MODE.READ:
        return (
          <Button
            type="link"
            htmlType="button"
            className={styles.ActionButton}
            disabled={disabled}
            onClick={() => {
              fireEditOrderSyncTrackEvent(
                makeSelectedKeyArray(defaultFinancialStatuses),
              );
              setMode(MODE.EDIT);
            }}
          >
            {I18n.t('v2.commonButton.edit')}
          </Button>
        );
    }
  };

  return (
    <Spin spinning={loading}>
      <PageContentPanel
        className={styles.ShopFinancialStatusSettingsSection}
        header={I18n.t('shopify.settings.financialStatusSection.header')}
        message={I18n.t('shopify.settings.financialStatusSection.message')}
      >
        <div className={styles.OptionButtonsContainer}>
          {Object.entries(FIELD_NAME).map(([key, label]) => (
            <Button
              className={cx(styles.OptionButton, {
                [styles.OptionButtonSelected]: statusForm[key],
              })}
              key={key}
              type="primary"
              shape="round"
              size={'default'}
              disabled={shouldDisableFormField}
              onClick={() => toggleOption(key)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className={styles.ActionButtonsContainer}>
          {makeActionButtons(mode, loading)}
        </div>
      </PageContentPanel>
    </Spin>
  );
};

ShopFinancialStatusSettingsSection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  countryCode: PropTypes.string.isRequired,
};

export default connect(null)(ShopFinancialStatusSettingsSection);
